import React, { useEffect, useMemo, useState } from "react";
import { api, formatMoney, formatApiError, todayISO, formatDateTime } from "../lib/api";
import { toast } from "sonner";
import {
  Fuel, Truck, Save, Plus, AlertTriangle, TrendingDown, TrendingUp, Gauge,
  RefreshCcw, Trash2, X, FileDown, Pencil, UserCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

function VehicleModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", number: "", expected_avg_kmpl: 0, notes: "", active: true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.number.trim()) { toast.error("Name and number required"); return; }
    setBusy(true);
    try { await api.post("/vehicles", f); toast.success("Vehicle added"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="vehicle-modal">
        <div className="p-5 border-b flex justify-between">
          <div className="font-heading font-black text-xl">Add Vehicle</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="small-label block mb-1">Name / description</label>
            <input className="input-lg" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="veh-name" placeholder="Tata Ace · Ambhai run" />
          </div>
          <div>
            <label className="small-label block mb-1">Number plate</label>
            <input className="input-lg" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="veh-number" placeholder="MH14KQ8335" />
          </div>
          <div>
            <label className="small-label block mb-1">Expected mileage (km/L)</label>
            <input type="number" step="0.1" className="input-lg text-right data-num" value={f.expected_avg_kmpl} onChange={(e) => setF({ ...f, expected_avg_kmpl: Number(e.target.value) })} data-testid="veh-kmpl" />
          </div>
          <div>
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-5 flex items-center gap-2" data-testid="veh-save"><Save size={16} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ row, vehicles, onClose, onSaved }) {
  const [f, setF] = useState({
    date: row.date || "",
    vehicle_id: row.vehicle_id || "",
    trip_km: row.trip_km || 0,
    litres: row.litres || 0,
    amount: row.amount || 0,
    driver_name: row.driver_name || "",
    co_driver_name: row.co_driver_name || "",
    bill_number: row.bill_number || row.reference || "",
    description: row.description || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (Number(f.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      await api.patch(`/expenses/${row.id}`, {
        date: f.date,
        vehicle_id: f.vehicle_id,
        trip_km: Number(f.trip_km),
        litres: Number(f.litres),
        amount: Number(f.amount),
        driver_name: f.driver_name,
        co_driver_name: f.co_driver_name,
        bill_number: f.bill_number,
        reference: f.bill_number,
        description: f.description,
      });
      toast.success("Diesel entry updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="diesel-edit-modal">
        <div className="p-5 border-b flex justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">Edit diesel entry</div>
            <div className="font-heading font-black text-xl">{row.vehicle_number || "—"} · {row.date}</div>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="small-label block mb-1">Date</label>
            <input type="date" className="input-lg" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>
          <div>
            <label className="small-label block mb-1">Vehicle</label>
            <select className="input-lg" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })}>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.number} · {v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1">Trip km (odometer)</label>
            <input type="number" min="0" step="0.1" className="input-lg text-right data-num" value={f.trip_km}
              onChange={(e) => setF({ ...f, trip_km: e.target.value })} data-testid="edit-trip-km" />
          </div>
          <div>
            <label className="small-label block mb-1">Litres</label>
            <input type="number" min="0" step="0.01" className="input-lg text-right data-num" value={f.litres}
              onChange={(e) => setF({ ...f, litres: e.target.value })} data-testid="edit-litres" />
          </div>
          <div>
            <label className="small-label block mb-1">Amount ₹</label>
            <input type="number" min="0" step="0.01" className="input-lg text-right data-num" value={f.amount}
              onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="edit-amount" />
          </div>
          <div>
            <label className="small-label block mb-1">Bill number</label>
            <input className="input-lg" value={f.bill_number} onChange={(e) => setF({ ...f, bill_number: e.target.value })} />
          </div>
          <div>
            <label className="small-label block mb-1">Driver</label>
            <input className="input-lg" value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} />
          </div>
          <div>
            <label className="small-label block mb-1">Co-driver</label>
            <input className="input-lg" value={f.co_driver_name} onChange={(e) => setF({ ...f, co_driver_name: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
        </div>
        <div className="p-5 border-t sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-5 flex items-center gap-2" data-testid="edit-save">
            <Save size={16} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Diesel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [holders, setHolders] = useState([]);
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [vehModal, setVehModal] = useState(false);
  const [vehFilter, setVehFilter] = useState("");
  const [from, setFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(todayISO());

  const [form, setForm] = useState({
    date: todayISO(),
    vehicle_id: "",
    holder_id: "",
    trip_km: 0,   // current odometer reading
    litres: 0,
    amount: 0,
    driver_name: "",
    co_driver_name: "",
    bill_number: "",
    description: "",
  });
  const [busy, setBusy] = useState(false);
  const [prevOdo, setPrevOdo] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const loadMasters = async () => {
    const [v, h] = await Promise.all([api.get("/vehicles"), api.get("/cash-holders")]);
    setVehicles(v.data); setHolders(h.data);
    if (h.data[0] && !form.holder_id) setForm((f) => ({ ...f, holder_id: h.data[0].id }));
    if (v.data[0] && !form.vehicle_id) setForm((f) => ({ ...f, vehicle_id: v.data[0].id }));
  };
  const loadLog = async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to, limit: 1000 };
      if (vehFilter) params.vehicle_id = vehFilter;
      const { data } = await api.get("/diesel/entries", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadMasters(); }, []);
  useEffect(() => { loadLog(); }, [from, to, vehFilter]);

  // Fetch last odometer whenever vehicle changes
  useEffect(() => {
    if (!form.vehicle_id) { setPrevOdo(null); return; }
    api.get(`/vehicles/${form.vehicle_id}/last-odometer`)
      .then((r) => setPrevOdo(r.data?.last_odometer || 0))
      .catch(() => setPrevOdo(null));
  }, [form.vehicle_id]);

  const kmRun = useMemo(() => Math.max(0, Number(form.trip_km || 0) - Number(prevOdo || 0)), [form.trip_km, prevOdo]);
  const computedKmpl = useMemo(() => (kmRun > 0 && Number(form.litres) > 0) ? Math.round((kmRun / Number(form.litres)) * 100) / 100 : 0, [kmRun, form.litres]);
  const selectedVeh = vehicles.find((v) => v.id === form.vehicle_id);
  const expected = Number(selectedVeh?.expected_avg_kmpl || 0);
  const dev = expected > 0 && computedKmpl > 0 ? Math.round(((computedKmpl - expected) * 100 / expected) * 10) / 10 : 0;

  const submit = async () => {
    if (!form.vehicle_id) { toast.error("Vehicle required"); return; }
    if (!form.holder_id) { toast.error("Cash holder required"); return; }
    if (Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      await api.post("/expenses", {
        date: form.date,
        holder_id: form.holder_id,
        category: "Diesel",
        amount: Number(form.amount),
        description: form.description,
        reference: form.bill_number,
        vehicle_id: form.vehicle_id,
        trip_km: Number(form.trip_km) || null,
        litres: Number(form.litres) || null,
        driver_name: form.driver_name,
        co_driver_name: form.co_driver_name,
        bill_number: form.bill_number,
      });
      toast.success("Diesel entry saved");
      setForm({ ...form, trip_km: 0, litres: 0, amount: 0, driver_name: "", co_driver_name: "", bill_number: "", description: "" });
      loadLog();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this diesel entry?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); loadLog(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="diesel-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Fuel size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Diesel</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Vehicle-wise fills · odometer tank-to-tank kmpl · deviation alerts
        </span>
        <button onClick={() => setVehModal(true)} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold flex items-center gap-1.5 hover:border-black" data-testid="add-vehicle">
          <Plus size={13} /> Add Vehicle
        </button>
      </div>

      {/* Entry form */}
      <div className="card-flat p-5" data-testid="diesel-entry-form">
        <div className="small-label text-gray-500 mb-3">New diesel entry</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="small-label block mb-1">Date</label>
            <input type="date" className="input-lg" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="diesel-date" />
          </div>
          <div>
            <label className="small-label block mb-1">Vehicle</label>
            <select className="input-lg" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="diesel-vehicle">
              <option value="">— Select —</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.number} · {v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1">Cash from</label>
            <select className="input-lg" value={form.holder_id} onChange={(e) => setForm({ ...form, holder_id: e.target.value })} data-testid="diesel-holder">
              {holders.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1">Bill number</label>
            <input className="input-lg" value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} data-testid="diesel-bill" placeholder="petrol pump bill no" />
          </div>

          <div>
            <label className="small-label block mb-1">Trip km (current odometer)</label>
            <input type="number" min="0" step="0.1" className="input-lg text-right data-num" value={form.trip_km}
              onChange={(e) => setForm({ ...form, trip_km: Number(e.target.value) })} data-testid="diesel-odo" />
            {prevOdo != null && prevOdo > 0 && (
              <div className="text-[11px] text-gray-500 mt-0.5">Last recorded: <span className="data-num font-bold">{formatMoney(prevOdo)}</span> km · run <span className="data-num font-bold">{formatMoney(kmRun)}</span> km</div>
            )}
          </div>
          <div>
            <label className="small-label block mb-1">Litres</label>
            <input type="number" min="0" step="0.01" className="input-lg text-right data-num" value={form.litres}
              onChange={(e) => setForm({ ...form, litres: Number(e.target.value) })} data-testid="diesel-litres" />
          </div>
          <div>
            <label className="small-label block mb-1">Amount ₹</label>
            <input type="number" min="0" step="0.01" className="input-lg text-right data-num" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} data-testid="diesel-amount" />
          </div>
          <div>
            <label className="small-label block mb-1">Live kmpl</label>
            <div className={`input-lg flex items-center justify-end data-num font-black ${computedKmpl > 0 && Math.abs(dev) >= 15 ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-gray-50"}`}
              data-testid="diesel-live-kmpl">
              {computedKmpl > 0 ? computedKmpl : "—"}
              {expected > 0 && computedKmpl > 0 && (
                <span className="text-[11px] font-medium ml-2 text-gray-500">exp {expected} · dev {dev > 0 ? "+" : ""}{dev}%</span>
              )}
            </div>
          </div>

          <div>
            <label className="small-label block mb-1">Driver</label>
            <input className="input-lg" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="diesel-driver" />
          </div>
          <div>
            <label className="small-label block mb-1">Co-driver</label>
            <input className="input-lg" value={form.co_driver_name} onChange={(e) => setForm({ ...form, co_driver_name: e.target.value })} data-testid="diesel-codriver" />
          </div>
          <div className="lg:col-span-2">
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={submit} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="diesel-save">
            <Save size={16} /> {busy ? "Saving…" : "Save entry"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5"><div className="small-label text-gray-500">Entries</div><div className="data-num text-3xl mt-1">{data.summary.count || 0}</div></div>
        <div className="card-flat p-5 bg-black text-white border-black"><div className="small-label text-white/70">Total spend</div><div className="data-num text-3xl mt-1">₹ {formatMoney(data.summary.total_amount || 0)}</div></div>
        <div className="card-flat p-5"><div className="small-label text-gray-500">Litres</div><div className="data-num text-3xl mt-1">{formatMoney(data.summary.total_litres || 0)}</div></div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]"><div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12}/> Deviation alerts</div><div className="data-num text-3xl mt-1 text-[#B91C1C]">{data.summary.with_alert || 0}</div></div>
      </div>

      {/* Filters + log */}
      <div className="card-flat">
        <div className="p-4 border-b flex items-center gap-3 flex-wrap">
          <select className="input-lg h-9 w-56" value={vehFilter} onChange={(e) => setVehFilter(e.target.value)} data-testid="diesel-filter-vehicle">
            <option value="">All vehicles</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.number} · {v.name}</option>)}
          </select>
          <input type="date" className="input-lg h-9" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="diesel-from" />
          <span>→</span>
          <input type="date" className="input-lg h-9" value={to} onChange={(e) => setTo(e.target.value)} data-testid="diesel-to" />
          <button onClick={loadLog} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold flex items-center gap-1.5 hover:border-black">
            <RefreshCcw size={13} /> Refresh
          </button>
        </div>
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Vehicle</th>
                <th className="num">Odo (km)</th>
                <th className="num">Litres</th>
                <th className="num">Amt ₹</th>
                <th className="num">kmpl</th>
                <th className="text-left">Driver / Co-driver</th>
                <th className="text-left">Bill</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm">Loading…</td></tr>}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm">No diesel entries.</td></tr>
              )}
              {!loading && data.rows.map((r) => {
                const isLow = r.deviation_pct < 0 && Math.abs(r.deviation_pct) >= 15;
                const isHigh = r.deviation_pct > 15;
                return (
                  <tr key={r.id} data-testid={`diesel-row-${r.id}`} className={(isLow || isHigh) ? "bg-[#FEE2E2]/30" : ""}>
                    <td className="data-num text-xs">{r.date}</td>
                    <td>
                      <div className="font-semibold text-sm">{r.vehicle_number || "—"}</div>
                      <div className="text-xs text-gray-500">{r.vehicle_name}</div>
                      {(r.created_by_name || r.created_by_email) && (
                        <div className="text-[10px] text-gray-400 mt-0.5 inline-flex items-center gap-1" data-testid={`diesel-entered-by-${r.id}`}>
                          <UserCheck size={10} />
                          {r.created_by_name || r.created_by_email}
                          {r.created_at ? ` · ${formatDateTime(r.created_at)}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="num data-num">{r.trip_km || ""}</td>
                    <td className="num data-num">{r.litres || ""}</td>
                    <td className="num font-bold">{formatMoney(r.amount)}</td>
                    <td className="num">
                      <div className="data-num font-bold">{r.actual_avg_kmpl || "—"}</div>
                      {r.expected_avg_kmpl > 0 && r.actual_avg_kmpl > 0 && (
                        <div className={`text-[10px] flex items-center gap-0.5 justify-end ${isLow ? "text-[#B91C1C]" : isHigh ? "text-[#B45309]" : "text-gray-500"}`}>
                          {isLow ? <TrendingDown size={10} /> : isHigh ? <TrendingUp size={10} /> : <Gauge size={10} />}
                          {r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%
                        </div>
                      )}
                    </td>
                    <td className="text-xs">
                      {r.driver_name || <span className="text-gray-400">—</span>}
                      {r.co_driver_name && <div className="text-gray-500">+ {r.co_driver_name}</div>}
                    </td>
                    <td className="text-xs data-num">{r.bill_number || r.reference || <span className="text-gray-400">—</span>}</td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => setEditRow(r)} className="text-gray-400 hover:text-black" title="Edit" data-testid={`diesel-edit-${r.id}`}>
                          <Pencil size={13} />
                        </button>
                        {isAdmin && (
                          <button onClick={() => del(r.id)} className="text-gray-400 hover:text-[#B91C1C]" data-testid={`diesel-del-${r.id}`}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {vehModal && <VehicleModal onClose={() => setVehModal(false)} onSaved={() => { setVehModal(false); loadMasters(); }} />}
      {editRow && <EditModal row={editRow} vehicles={vehicles} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); loadLog(); }} />}
      <Truck size={0} /><FileDown size={0} />
    </div>
  );
}
